import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { deletingSideEffects, errorSideEffects, loadingSideEffects, successSideEffects, updatingSideEffects } from "..";
import { toastGetError, Toastify } from "../../../components/Toaster";
import { ObjectSchema, SideEffectSchema } from "../../actions";
import { getOutcomeTrackingState, getRequestTrackingState, MutationTrackingState } from "../../../services/analytics/tracking";

export interface CustomDomainMapping {
  ID: string;
  AppID: number;
  TemplateId: string | null;
  Domain: string;
  IsDone: boolean;
  StyleName: string | null;
  IsDefault?: boolean;
}

export interface MapDomainTemplate {
  TemplateId: string;
  StyleName?: string | null;
}

export interface CustomDomainMappingSchema extends SideEffectSchema {
  Data: CustomDomainMapping[];
  isLoading: boolean;
  isUpdating: boolean;
  isAdding: boolean;
  isDeleting: boolean;
  errorMessage: string | null;
}

export const CustomDomainMappingSlice = createSlice({
  name: "customDomainMapping",
  initialState: {
    Data: [],
    isLoading: false,
    isUpdating: false,
    isAdding: false,
    isDeleting: false,
    isError: false,
    isSuccess: false,
    errorMessage: null
  } as CustomDomainMappingSchema,
  reducers: {
    getCustomDomainMappingRequest(state) {
      Object.assign(state, loadingSideEffects);
    },
    getCustomDomainMappingSuccess(state, action: PayloadAction<CustomDomainMappingSchema>) {
      Object.assign(state, successSideEffects);
      state.Data = action.payload.Data;
    },
    getCustomDomainMappingFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
      if (action.payload) {
        toastGetError(action.payload);
      }
    },
    updateCustomDomainMappingRequest(state, action: PayloadAction<ObjectSchema<MapDomainTemplate>>) {
      Object.assign(state, updatingSideEffects, getRequestTrackingState(action));
    },
    updateCustomDomainMappingSuccess(state, action: PayloadAction<ObjectSchema<MapDomainTemplate>>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      const payload = action.payload;
      for (const domain in payload) {
        const index = state.Data.findIndex((item) => item.Domain === domain);
        if (index !== -1) {
          state.Data[index].TemplateId = payload[domain].TemplateId;
          state.Data[index].StyleName = payload[domain].StyleName;
        }
      }
      Toastify("success", "Custom domain updated successfully");
    },
    updateCustomDomainMappingFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },

    deleteCustomDomainMappingRequest(state, action: PayloadAction<string>) {
      Object.assign(state, deletingSideEffects, getRequestTrackingState(action));
    },
    deleteCustomDomainMappingSuccess(state, action: PayloadAction<string>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      const index = state.Data.findIndex((item) => item.Domain === action.payload);
      if (index !== -1) {
        state.Data[index].TemplateId = null;
        state.Data[index].StyleName = null;
      }
      Toastify("success", "Custom domain deleted successfully");
    },
    deleteCustomDomainMappingFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    }
  }
});

export const {
  getCustomDomainMappingRequest,
  getCustomDomainMappingSuccess,
  getCustomDomainMappingFailure,
  updateCustomDomainMappingRequest,
  updateCustomDomainMappingSuccess,
  updateCustomDomainMappingFailure,
  deleteCustomDomainMappingRequest,
  deleteCustomDomainMappingSuccess,
  deleteCustomDomainMappingFailure
} = CustomDomainMappingSlice.actions;

export default CustomDomainMappingSlice.reducer;
